import {
    Box,
    CircularProgress,
    Button as MuiButton,
    ButtonProps as MuiButtonProps,
    styled,
    Tooltip,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router';
import { useValueRef } from 'src/helpers/react';
import { useTrack } from 'src/lib/analytics';

import {
    AnalyticsEvent,
    AnalyticsEventType,
    ClickElementType,
    DisplayElementType,
} from '../interactionTypes';
import { Typography } from './Typography';

export type ButtonKind = 'filled' | 'outlined' | 'text';

export type ButtonColor = 'primary' | 'secondary' | 'critical' | 'success' | 'inherit';

export type ButtonSize = 'small' | 'standard' | 'large';

const StyledButton = styled(MuiButton, {
    shouldForwardProp: (prop) => prop !== '$splitButtonArrow' && prop !== '$size',
})<{ $splitButtonArrow: boolean; $size: ButtonSize }>`
    padding: ${({ theme, $splitButtonArrow, $size }) => {
        const sizeFactor = $size === 'small' ? 0.75 : $size === 'large' ? 2.75 : 1;
        const rightLeft = $splitButtonArrow ? 1 : 2;
        return theme.spacing(1 * sizeFactor, rightLeft * sizeFactor);
    }};
    max-height: ${({ theme, $size }) => {
        return $size === 'small' ? theme.spacing(3.375) : theme.spacing(4.25);
    }};
    &.MuiButton-containedPrimary {
        color: ${({ theme }) => theme.resqPalette.text.onPrimary};
        background-color: ${({ theme }) => theme.resqPalette.surface.primary};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.primaryHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.primaryPressed};
        }
        &[disabled] {
            background-color: ${({ theme }) => theme.resqPalette.surface.primaryDisabled};
        }
    }
    &.MuiButton-containedSecondary {
        color: ${({ theme }) => theme.resqPalette.text.onSecondary};
        background-color: ${({ theme }) => theme.resqPalette.surface.secondary};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.secondaryHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.secondaryPressed};
        }
        &[disabled] {
            background-color: ${({ theme }) => theme.resqPalette.surface.secondaryDisabled};
            color: ${({ theme }) => theme.resqPalette.text.disabled};
        }
    }
    &.MuiButton-containedDanger {
        color: ${({ theme }) => theme.resqPalette.text.onCritical};
        background-color: ${({ theme }) => theme.resqPalette.surface.critical};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalPressed};
        }
        &[disabled] {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalDisabled};
        }
    }
    &.MuiButton-containedSuccess {
        color: ${({ theme }) => theme.resqPalette.text.onSuccess};
        background-color: ${({ theme }) => theme.resqPalette.surface.success};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.successHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.successPressed};
        }
        &[disabled] {
            background-color: ${({ theme }) => theme.resqPalette.surface.successDisabled};
        }
    }
    &.MuiButton-outlinedPrimary {
        color: ${({ theme }) => theme.resqPalette.text.primary};
        border-color: ${({ theme }) => theme.resqPalette.border.primary};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.primarySubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.primarySubduedPressed};
            color: ${({ theme }) => theme.resqPalette.text.primaryPressed};
        }
        &[disabled] {
            border-color: ${({ theme }) => theme.resqPalette.border.primaryDisabled};
            color: ${({ theme }) => theme.resqPalette.text.primaryDisabled};
            opacity: 0.56;
        }
    }
    &.MuiButton-outlinedSecondary {
        color: ${({ theme }) => theme.resqPalette.text.default};
        border-color: ${({ theme }) => theme.resqPalette.border.secondary};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.secondaryDisabled};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.secondary};
        }
        &[disabled] {
            border-color: ${({ theme }) => theme.resqPalette.border.secondaryDisabled};
            color: ${({ theme }) => theme.resqPalette.text.disabled};
            opacity: 0.56;
        }
    }
    &.MuiButton-outlinedDanger {
        color: ${({ theme }) => theme.resqPalette.text.critical};
        border-color: ${({ theme }) => theme.resqPalette.border.critical};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalSubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalSubduedPressed};
            color: ${({ theme }) => theme.resqPalette.text.criticalPressed};
        }
        &[disabled] {
            border-color: ${({ theme }) => theme.resqPalette.border.criticalDisabled};
            color: ${({ theme }) => theme.resqPalette.text.criticalDisabled};
            opacity: 0.56;
        }
    }
    &.MuiButton-outlinedSuccess {
        color: ${({ theme }) => theme.resqPalette.text.success};
        border-color: ${({ theme }) => theme.resqPalette.border.success};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.successSubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.successSubduedPressed};
            color: ${({ theme }) => theme.resqPalette.text.successPressed};
        }
        &[disabled] {
            border-color: ${({ theme }) => theme.resqPalette.border.successDisabled};
            color: ${({ theme }) => theme.resqPalette.text.successDisabled};
            opacity: 0.56;
        }
    }
    &.MuiButton-textPrimary {
        color: ${({ theme }) => theme.resqPalette.text.primary};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.primarySubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.primarySubduedPressed};
        }
        &[disabled] {
            color: ${({ theme }) => theme.resqPalette.text.primaryDisabled};
        }
    }
    &.MuiButton-textSecondary {
        color: ${({ theme }) => theme.resqPalette.text.default};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.secondaryDisabled};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.secondary};
        }
        &[disabled] {
            color: ${({ theme }) => theme.resqPalette.text.disabled};
        }
    }
    &.MuiButton-textDanger {
        color: ${({ theme }) => theme.resqPalette.text.critical};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalSubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalSubduedPressed};
        }
        &[disabled] {
            color: ${({ theme }) => theme.resqPalette.text.criticalDisabled};
        }
    }
    &.MuiButton-textSuccess {
        color: ${({ theme }) => theme.resqPalette.text.success};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.successSubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.successSubduedPressed};
        }
        &[disabled] {
            color: ${({ theme }) => theme.resqPalette.text.successDisabled};
        }
    }
`;

export type BaseButtonProps = Pick<
    MuiButtonProps,
    | 'autoFocus'
    | 'children'
    | 'onClick'
    | 'onMouseDown'
    | 'type'
    | 'fullWidth'
    | 'startIcon'
    | 'endIcon'
    | 'disabled'
    | 'sx'
    | 'style'
    | 'className'
> & {
    size?: ButtonSize;
    trackingId?: string;
    trackingProperties?: Record<string, unknown>;
    loading?: boolean;
    splitButtonArrow?: boolean;
    disabledTooltipLabel?: string;
    dataQa?: string;
    to?: string;
    href?: string;
    target?: string;
    rel?: string;
    download?: string | boolean;
    'aria-expanded'?: boolean;
    'aria-describedby'?: string;
};

export type FilledButtonProps = {
    kind: 'filled';
    color: ButtonColor;
};

export type NonFilledButtonProps = {
    kind: Exclude<ButtonKind, 'filled'>;
    color: ButtonColor;
};

export type ButtonProps = BaseButtonProps & (FilledButtonProps | NonFilledButtonProps);

function mapVariant(kind: ButtonKind) {
    switch (kind) {
        case 'filled':
            return 'contained';
        default:
            return kind;
    }
}

function mapColor(color: ButtonColor) {
    switch (color) {
        case 'critical':
            return 'danger';
        default:
            return color;
    }
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            kind,
            color,
            size = 'standard',
            onClick,
            type = 'button',
            fullWidth,
            startIcon,
            endIcon,
            disabled,
            disabledTooltipLabel,
            trackingId,
            trackingProperties,
            loading,
            splitButtonArrow = false,
            dataQa,
            to,
            autoFocus,
            sx,
            style,
            className,
            onMouseDown,
            href,
            target,
            rel,
            download,
            'aria-expanded': ariaExpanded,
            'aria-describedby': ariaDescribedBy,
        },
        ref,
    ) => {
        const track = useTrack();
        const trackRef = useValueRef(track);
        const trackingPropertiesRef = useValueRef(trackingProperties);
        const disabledRef = useValueRef(disabled);
        useEffect(() => {
            trackRef.current(AnalyticsEvent.ElementDisplayed, {
                ...trackingPropertiesRef.current,
                trackingId: trackingId,
                label: splitButtonArrow ? 'Arrow Icon' : children?.toString() || null,
                elementType: DisplayElementType.BUTTON,
                eventType: AnalyticsEventType.ANALYTICS,
                link: null,
                option: null,
                disabled: disabledRef.current,
            });
        }, [children, splitButtonArrow, disabledRef, trackRef, trackingId, trackingPropertiesRef]);

        const button = React.createElement(
            StyledButton as any,
            {
                ref,
                type,
                $size: size,
                variant: mapVariant(kind),
                color: mapColor(color),
                fullWidth,
                startIcon: loading ? <CircularProgress size={17} color="inherit" /> : startIcon,
                endIcon,
                disabled: disabled || loading,
                autoFocus,
                sx,
                style,
                className,
                $splitButtonArrow: splitButtonArrow,
                disableRipple: true,
                disableElevation: true,
                component: to ? RouterLink : undefined,
                to,
                onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                    onClick?.(event);
                    track(AnalyticsEvent.ElementClicked, {
                        ...trackingProperties,
                        trackingId,
                        label: splitButtonArrow ? 'Arrow Icon' : children?.toString() || null,
                        elementType: ClickElementType.BUTTON,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                        link: to ?? null,
                    });
                },
                onMouseDown,
                href,
                target,
                rel,
                download,
                'aria-expanded': ariaExpanded,
                'aria-describedby': ariaDescribedBy,
                'data-button-name': children?.toString(),
                'data-qa': dataQa,
            },
            <Typography role={size === 'small' ? 'button-small' : 'button'} color="inherit">
                {children}
            </Typography>,
        );

        if (disabled && disabledTooltipLabel) {
            return (
                <Box display="flex">
                    <Tooltip title={disabledTooltipLabel} arrow>
                        <Box>{button}</Box>
                    </Tooltip>
                </Box>
            );
        }

        return button;
    },
);
